import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import projectService from '../../services/projectService';
import './ProjectDetail.css';

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProject();
    fetchTasks();
  }, [id]);

  const fetchProject = async () => {
    try {
      setLoading(true);
      const data = await projectService.getProjectById(id);
      setProject(data.project);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch project');
    } finally {
      setLoading(false);
    }
  };

  const fetchTasks = async () => {
    try {
      const data = await projectService.getProjectTasks(id);
      setTasks(data.tasks);
    } catch (err) {
      console.error('Failed to fetch tasks:', err);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this project?')) {
      return;
    }

    try {
      await projectService.deleteProject(id);
      navigate('/app/projects');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete project');
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'Planning': 'badge-secondary',
      'In Progress': 'badge-primary',
      'On Hold': 'badge-warning',
      'Completed': 'badge-success',
      'Cancelled': 'badge-danger'
    };
    return statusColors[status] || 'badge-secondary';
  };

  const getPriorityBadge = (priority) => {
    const priorityColors = {
      'Low': 'badge-info',
      'Medium': 'badge-warning',
      'High': 'badge-danger',
      'Critical': 'badge-danger-dark'
    };
    return priorityColors[priority] || 'badge-secondary';
  };

  if (loading) {
    return <div className="loading">Loading project...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="alert alert-danger">{error}</div>
        <Link to="/app/projects" className="btn btn-primary">Back to Projects</Link>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="error-container">
        <div className="alert alert-warning">Project not found</div>
        <Link to="/app/projects" className="btn btn-primary">Back to Projects</Link>
      </div>
    );
  }

  const budgetRemaining = project.budget_allocated - project.budget_spent;
  const budgetPercentage = project.budget_allocated > 0 
    ? (project.budget_spent / project.budget_allocated * 100).toFixed(1)
    : 0;

  return (
    <div className="project-detail-container">
      <div className="project-detail-header">
        <div className="header-left">
          <Link to="/app/projects" className="back-link">
            <i className="fas fa-arrow-left"></i> Back to Projects
          </Link>
          <div className="project-title-section">
            <h1>{project.name}</h1>
            <span className="project-code">{project.code}</span>
          </div>
        </div>
        <div className="header-actions">
          <Link to={`/app/projects/${id}/edit`} className="btn btn-outline-primary">
            <i className="fas fa-edit"></i> Edit
          </Link>
          <button onClick={handleDelete} className="btn btn-outline-danger">
            <i className="fas fa-trash"></i> Delete
          </button>
        </div>
      </div>

      <div className="project-detail-content">
        <div className="main-section">
          <div className="info-card">
            <h2>Project Information</h2>
            <div className="info-grid">
              <div className="info-item">
                <label>Status</label>
                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(project.status)}`}>
                  {project.status}
                </span>
              </div>

              <div className="info-item">
                <label>Priority</label>
                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getPriorityBadge(project.priority)}`}>
                  {project.priority}
                </span>
              </div>

              <div className="info-item">
                <label>Start Date</label>
                <span>
                  {project.start_date ? new Date(project.start_date).toLocaleDateString() : '-'}
                </span>
              </div>

              <div className="info-item">
                <label>End Date</label>
                <span>
                  {project.end_date ? new Date(project.end_date).toLocaleDateString() : '-'}
                </span>
              </div>

              {project.project_manager && (
                <div className="info-item">
                  <label>Project Manager</label>
                  <span>
                    {project.project_manager.first_name} {project.project_manager.last_name}
                  </span>
                </div>
              )}
            </div>

            {project.description && (
              <div className="description-section">
                <h3>Description</h3>
                <div
                  className="prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: project.description }}
                />
              </div>
            )}
          </div>

          <div className="info-card">
            <h2>Progress</h2>
            <div className="progress-section">
              <div className="progress-header">
                <span>Overall Progress</span>
                <span className="progress-value">{project.progress_percentage}%</span>
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${project.progress_percentage}%` }}
                ></div>
              </div>
            </div>
          </div>

          <div className="info-card">
            <h2>Budget</h2>
            <div className="budget-grid">
              <div className="budget-item">
                <label>Allocated</label>
                <span className="budget-value">
                  ${project.budget_allocated?.toLocaleString() || '0'}
                </span>
              </div>
              <div className="budget-item">
                <label>Spent</label>
                <span className="budget-value spent">
                  ${project.budget_spent?.toLocaleString() || '0'}
                </span>
              </div>
              <div className="budget-item">
                <label>Remaining</label>
                <span className={`budget-value ${budgetRemaining < 0 ? 'over-budget' : ''}`}>
                  ${budgetRemaining?.toLocaleString() || '0'}
                </span>
              </div>
            </div>
            {project.budget_allocated > 0 && (
              <div className="budget-progress">
                <div className="progress-bar">
                  <div 
                    className={`progress-fill ${budgetPercentage > 100 ? 'over-budget' : ''}`}
                    style={{ width: `${Math.min(budgetPercentage, 100)}%` }}
                  ></div>
                </div>
                <span className="budget-percentage">{budgetPercentage}% used</span>
              </div>
            )}
          </div>

          <div className="info-card">
            <div className="card-header">
              <h2>Tasks ({tasks.length})</h2>
              <Link to={`/app/tasks/new?project_id=${id}`} className="btn btn-sm btn-primary">
                <i className="fas fa-plus"></i> Add Task
              </Link>
            </div>
            {tasks.length === 0 ? (
              <p className="text-muted">No tasks yet</p>
            ) : (
              <div className="tasks-list">
                {tasks.slice(0, 5).map(task => (
                  <div key={task.id} className="task-item">
                    <Link to={`/app/tasks/${task.id}`} className="task-title">
                      {task.title}
                    </Link>
                    <div className="task-meta">
                      <span className={`badge badge-sm ${getStatusBadge(task.status)}`}>
                        {task.status}
                      </span>
                      <span className={`badge badge-sm ${getPriorityBadge(task.priority)}`}>
                        {task.priority}
                      </span>
                    </div>
                  </div>
                ))}
                {tasks.length > 5 && (
                  <Link to={`/app/tasks?project_id=${id}`} className="view-all-link">
                    View all {tasks.length} tasks →
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;


